import { Validators } from "@angular/forms";
import { CustomValidators } from "src/app/core/forms/validators/custom-validators";
import { FormConfig } from "src/app/core/forms/engine/core-types";
import { ProductLotEntry } from "../models/add-stock-manually-model";
import { TIRE_PRODUCT_FORM_CONFIG } from "./tire-product-form.config";
import { ProductCategoryEnum } from "src/app/core/enums/product-category.enum";

/**
 * CONFIGURACIÓN DEL FORMULARIO DE AGREGAR STOCK
 * ═════════════════════════════════════════════════════════════════
 * 
 * Esta configuración define la estructura, validadores y metadatos
 * del formulario para agregar stock manualmente a almacenes.
 * 
 * Sigue la arquitectura centralizada de formularios del proyecto.
 * Para utilizar este config, inyecta FormBuilderService y crea
 * el formulario como:
 * 
 *   this.form = this.formBuilder.create<TyreProductLotEntry>(
 *     ADD_STOCK_FORM_CONFIG
 *   );
 */

// import formBuilder from   ← no puedes usar servicio en un archivo de configuración

export const ADD_STOCK_FORM_CONFIG: FormConfig<ProductLotEntry> = {
  name: 'addStockForm',
  description: 'Formulario para agregar stock manualmente a almacenes',

  fields: {
    // ═══════════════════════════════════════════════════════════════
    // CAMPOS DE SELECCIÓN DE ALMACÉN Y PRODUCTO
    // ═══════════════════════════════════════════════════════════════

    storeId: {
      type: 'select',
      label: 'Almacén',
      placeholder: 'Selecciona un almacén',
      required: true,
      validators: [],
      description: 'Almacén donde se agregará el stock',
      options: [],
    },

    productId: {
      type: 'select',
      label: 'Producto',
      placeholder: 'Selecciona un producto existente o crea uno nuevo',
      required: false, // Puede ser opcional si es un producto nuevo
      validators: [],
      description: 'Producto existente. Si no existe, crea uno nuevo abajo',
      options: [],
    },

    productSearch: {
      type: 'text',
      label: 'Buscar Producto',
      placeholder: 'Ej: Michelin Pilot, TYRE-SUMMER-001',
      required: false,
      validators: [Validators.maxLength(100)],
      description: 'Busca por nombre, código o descripción del producto',
    },

    brandSearch: {
      type: 'text',
      label: 'Buscar por Marca',
      placeholder: 'Ej: Michelin, Pirelli, Bridgestone',
      required: false,
      validators: [Validators.maxLength(100)],
      description: 'Filtra productos por marca',
    },

    productCategoryId: {
      type: 'number',
      label: 'Product Category Id',
      placeholder: '',
      initialValue: ProductCategoryEnum.TIRES,
      required: true,
      validators: [
        Validators.required,
      ],
      description: 'Tipo del producto',
    },

    quantity: {
      type: 'number',
      label: 'Cantidad',
      placeholder: 'Ej: 10',
      initialValue: 0,
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        CustomValidators.positive(),
        CustomValidators.integer(),
      ],
      description: 'Cantidad de unidades a agregar',
    },

    price: {
      type: 'number',
      label: 'Cantidad',
      placeholder: 'Ej: 10',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        CustomValidators.positive(),
      ],
      description: 'Precio del producto a agregar',
    },

    location: {
      type: 'array',
      label: 'Ubicaciones en Almacén',
      itemFormConfig: {
        name: 'location',
        fields: {},
      },
      required: false,
      validators: [],
      description: 'Secciones o ubicaciones específicas dentro del almacén',
    },

    dot: {
      type: 'text',
      label: 'DOT (Neumáticos)',
      placeholder: 'Ej: 1821 (semana y año)',
      required: true,
      validators: [
        Validators.maxLength(4),
        CustomValidators.pattern(
          /^\d{4}$/,
        ),
      ],
      description: 'Código DOT: últimos 4 dígitos (semana y año de fabricación)',
    },

    entryDate: {
      type: 'date',
      label: 'Fecha de Entrada',
      required: false,
      validators: [CustomValidators.noFutureDate()],
      description: 'Fecha en que el producto ingresó al almacén',
      errorMessages: {
        futureDateNotAllowed: 'addStock.futureDateNotAllowed',
      },
    },

    // ═══════════════════════════════════════════════════════════════
    // PRODUCTO NUEVO (FormGroup Anidado)
    // ═══════════════════════════════════════════════════════════════

    newTireProduct: {
      type: 'group',
      label: 'Crear Nuevo Producto',
      required: false,
      description: 'Completa los campos si deseas crear un producto nuevo',
      nestedFormConfig: TIRE_PRODUCT_FORM_CONFIG,
    },
  },

  // ═════════════════════════════════════════════════════════════════
  // VALIDADORES A NIVEL DE FORMULARIO
  // ═════════════════════════════════════════════════════════════════

  formValidators: [
    /**
     * Validador personalizado: Asegurar que al menos
     * productId O newTyreProduct esté presente
     */
    (formGroup) => {
      // Lógica: si productId es null, newProduct debe estar válido
      // Si productId tiene valor, newTyreProduct es opcional
      // Esto se puede implementar en un validador asíncrono
      return null; // Sin errores a nivel de formulario
    },
  ],

  conditionalRules: [
    // cuando la categoría no es neumáticos, siempre mantener el subgrupo deshabilitado
    {
      dependsOn: ['productCategoryId'],
      condition: values => values.productCategoryId !== ProductCategoryEnum.TIRES,
      thenActions: [
        { action: 'disable', fields: ['newTyreProduct'] }
      ],
      elseActions: [
        { action: 'enable', fields: ['newTyreProduct'] }
      ]
    },
    // si hay un producto seleccionado y es de categoría neumáticos, deshabilitamos el
    // formulario de creación para evitar validaciones innecesarias
    {
      dependsOn: ['productId', 'productCategoryId'],
      condition: (values) => !!values.productId && values.productCategoryId === ProductCategoryEnum.TIRES,
      thenActions: [
        { action: 'disable', fields: ['newTyreProduct'] }
      ],
      elseActions: [
        { action: 'enable', fields: ['newTyreProduct'] }
      ]
    }
  ],

  // ═════════════════════════════════════════════════════════════════
  // METADATOS DEL FORMULARIO
  // ═════════════════════════════════════════════════════════════════

  metadata: {
    category: 'stock-management',
    type: 'add-stock-manual',
    version: '1.0',
    lastUpdated: new Date('2025-03-02'),
    tags: ['stock', 'inventory', 'manual-entry', 'tyre-products'],
    documentation: {
      purpose: 'Formulario para agregar stock manualmente a almacenes',
      workflow: [
        '1. Selecciona el almacén destino',
        '2. Busca un producto existente o crea uno nuevo',
        '3. Especifica la cantidad a agregar',
        '4. Define ubicaciones dentro del almacén (opcional)',
        '5. Para neumáticos, ingresa DOT y fecha (opcional)',
        '6. Envía el formulario para guardar',
      ],
      notes: [
        'Si el producto no existe, puedes crear uno nuevo directamente',
        'Las ubicaciones pueden agregarse múltiples si es necesario',
        'Los campos DOT y entryDate son específicos para neumáticos',
      ],
    },
  },
};
